import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { ImageViewer } from 'antd-mobile';
import { IOpenMarketProduct } from '../../../interfaces/OpenMarketProduct.interfaces';
import { SwiperItemContainer } from '../../../components/common/itemContainer/SwiperItemContainer';
import { OpenMarketProductPreviewCardPageType } from './OpenMarketProductPreviewCard/OpenMarketProductPreviewCard';
import { GoToListPageButton } from '../../../components/common/buttons/GoToListPageButton';
import { Chip } from '../../../components/common/chip/Chip';
import { ampli } from '../../../ampli';

interface OpenMarketImageOnlySwiperProps {
  openMarketProducts: IOpenMarketProduct[];
  pageType: OpenMarketProductPreviewCardPageType;
}

const SLIDE_WIDTH = 300;
const SLIDE_HEIGHT = 255;

export const OpenMarketImageOnlySwiper = ({ openMarketProducts, pageType }: OpenMarketImageOnlySwiperProps) => {
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageViewerIndex, setCurrentImageViewerIndex] = useState(0);
  const [previousImageViewerIndex, setPreviousImageViewerIndex] = useState(0);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const imageViewerRef = useRef<{ swipeTo: (index: number) => void } | null>(null);
  const shouldCloseRef = useRef(false);

  useEffect(() => {
    if (!imageViewerVisible) {
      return undefined;
    }

    const cleanupFunctions: (() => void)[] = [];
    const touchStartInfo = new Map<Element, { x: number; y: number; time: number }>();
    const registeredElements = new Set<Element>();

    const setupEventListeners = () => {
      const selectors = [
        '#root-modal .adm-image-viewer',
        '#root-modal .adm-image-viewer-mask',
        '#root-modal .adm-image-viewer-content',
        '#root-modal .adm-image-viewer-slides',
        '#root-modal .adm-image-viewer-slide',
        '#root-modal .adm-image-viewer-image-wrapper',
        '#root-modal .adm-image-viewer-image',
      ];

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (registeredElements.has(element)) {
            return;
          }
          registeredElements.add(element);
          const handleClick = (e: Event) => {
            const target = e.target as HTMLElement;
            if (!target.closest('button') && !target.closest('[role="button"]')) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            }
          };

          const handleTouchStart = (e: Event) => {
            const touchEvent = e as TouchEvent;
            if (touchEvent.touches.length === 1) {
              const touch = touchEvent.touches[0];
              touchStartInfo.set(element, {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              });
            } else {
              touchStartInfo.delete(element);
            }
          };

          const handleTouchEnd = (e: Event) => {
            const touchEvent = e as TouchEvent;
            const target = touchEvent.target as HTMLElement;
            if (target.closest('button') || target.closest('[role="button"]')) {
              touchStartInfo.delete(element);
              return;
            }

            const startInfo = touchStartInfo.get(element);
            if (startInfo && touchEvent.changedTouches.length === 1) {
              const touch = touchEvent.changedTouches[0];
              const endX = touch.clientX;
              const endY = touch.clientY;
              const endTime = Date.now();

              const deltaX = Math.abs(endX - startInfo.x);
              const deltaY = Math.abs(endY - startInfo.y);
              const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
              const duration = endTime - startInfo.time;

              if (duration < 300 && distance < 10) {
                touchEvent.preventDefault();
                touchEvent.stopPropagation();
                touchEvent.stopImmediatePropagation();
              }
            }

            touchStartInfo.delete(element);
          };

          element.addEventListener('click', handleClick, true);
          element.addEventListener('click', handleClick, false);
          element.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
          element.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });
          element.addEventListener('touchend', handleTouchEnd, { capture: false, passive: false });

          cleanupFunctions.push(() => {
            element.removeEventListener('click', handleClick, true);
            element.removeEventListener('click', handleClick, false);
            element.removeEventListener('touchstart', handleTouchStart, true);
            element.removeEventListener('touchend', handleTouchEnd, true);
            element.removeEventListener('touchend', handleTouchEnd, false);
          });
        });
      });
    };

    setupEventListeners();
    const timer1 = setTimeout(setupEventListeners, 0);
    const timer2 = setTimeout(setupEventListeners, 50);
    const timer3 = setTimeout(setupEventListeners, 100);
    const timer4 = setTimeout(setupEventListeners, 200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      cleanupFunctions.forEach((cleanup) => cleanup());
      touchStartInfo.clear();
    };
  }, [imageViewerVisible]);

  const handleImageClick = (productIndex: number, openMarketProduct: IOpenMarketProduct) => {
    setSelectedProductIndex(productIndex);
    setCurrentImageViewerIndex(0);
    setPreviousImageViewerIndex(0);
    setImageViewerVisible(true);
    shouldCloseRef.current = false;

    ampli.pvmClickRealSellingModelOnMarketPrice({
      brand: openMarketProduct.machine.brand.name,
      model: openMarketProduct.machine.model,
      hp: openMarketProduct.machine.engineOutputInPs,
      selling_price: openMarketProduct.hopeSellingPrice,
    });
  };

  const handleImageViewerClose = () => {
    if (shouldCloseRef.current) {
      setImageViewerVisible(false);
      shouldCloseRef.current = false;
    }
  };

  const handleCloseButtonClick = () => {
    shouldCloseRef.current = true;
    setImageViewerVisible(false);
  };

  const handlePrevImage = () => {
    if (currentImageViewerIndex > 0) {
      const newIndex = currentImageViewerIndex - 1;
      setCurrentImageViewerIndex(newIndex);
      imageViewerRef.current?.swipeTo(newIndex);
    }
  };

  const handleNextImage = () => {
    const currentImages = getSortedImages(openMarketProducts[selectedProductIndex]);
    if (currentImageViewerIndex < currentImages.length - 1) {
      const newIndex = currentImageViewerIndex + 1;
      setCurrentImageViewerIndex(newIndex);
      imageViewerRef.current?.swipeTo(newIndex);
    }
  };

  const getSortedImages = (product: IOpenMarketProduct) => {
    return product.images
      .sort((a, b) => {
        if (a.type === 'LEFT') return -1;
        if (b.type === 'LEFT') return 1;
        return 0;
      })
      .map((image) => image.imageUrl);
  };

  const getMainImage = (product: IOpenMarketProduct) => {
    return product.images.find((image) => image.type === 'LEFT')?.imageUrl;
  };

  if (openMarketProducts.length > 1) {
    return (
      <>
        <Wrapper id={'open-market-product-swiper-container'}>
          <SwiperItemContainer
            slideItemWidth={SLIDE_WIDTH}
            slideItems={openMarketProducts.map((openMarketProduct, index) => (
              <ImageWrapper key={openMarketProduct.id}>
                <CustomImage
                  src={getMainImage(openMarketProduct)}
                  alt={getMainImage(openMarketProduct)}
                  onClick={() => handleImageClick(index, openMarketProduct)}
                />
                {openMarketProduct.status === 'ONSALE' && (
                  <ChipWrapper>
                    <Chip type={'square'} theme={'strong'} color={'primary'} size={'lg'}>
                      {'판매 중인 매물'}
                    </Chip>
                  </ChipWrapper>
                )}
              </ImageWrapper>
            ))}
            extraElement={{
              width: 150,
              element: <GoToListPageButton renderedIn={'odp'} itemType={'open-market'} height={SLIDE_HEIGHT} />,
            }}
          />
        </Wrapper>

        {imageViewerVisible && (
          <ImageViewer.Multi
            ref={imageViewerRef}
            visible={imageViewerVisible}
            images={getSortedImages(openMarketProducts[selectedProductIndex])}
            defaultIndex={currentImageViewerIndex}
            onClose={handleImageViewerClose}
            onIndexChange={(index) => {
              setPreviousImageViewerIndex(index);
              setCurrentImageViewerIndex(index);
            }}
            getContainer={() => document.getElementById('root-modal') || document.body}
            renderFooter={(image, index) => {
              const currentImages = getSortedImages(openMarketProducts[selectedProductIndex]);
              return (
                <ImageViewerControls>
                  <ImageViewerTopBar>
                    <CloseButton onClick={handleCloseButtonClick}>{'✕'}</CloseButton>
                  </ImageViewerTopBar>
                  <NavButtonLeft onClick={handlePrevImage} disabled={index === 0}>
                    <svg
                      width={'24'}
                      height={'24'}
                      viewBox={'0 0 24 24'}
                      fill={'none'}
                      xmlns={'http://www.w3.org/2000/svg'}
                    >
                      <path
                        d={'M15 18L9 12L15 6'}
                        stroke={'currentColor'}
                        strokeWidth={'2'}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                      />
                    </svg>
                  </NavButtonLeft>
                  <NavButtonRight onClick={handleNextImage} disabled={index === currentImages.length - 1}>
                    <svg
                      width={'24'}
                      height={'24'}
                      viewBox={'0 0 24 24'}
                      fill={'none'}
                      xmlns={'http://www.w3.org/2000/svg'}
                    >
                      <path
                        d={'M9 18L15 12L9 6'}
                        stroke={'currentColor'}
                        strokeWidth={'2'}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                      />
                    </svg>
                  </NavButtonRight>
                  <ImageViewerBottomBar>
                    <ImageCounter>{`${index + 1} / ${currentImages.length}`}</ImageCounter>
                  </ImageViewerBottomBar>
                </ImageViewerControls>
              );
            }}
          />
        )}
      </>
    );
  }

  if (openMarketProducts.length === 1) {
    return (
      <>
        <SingleImageWrapper>
          <CustomImage
            src={getMainImage(openMarketProducts[0])}
            alt={getMainImage(openMarketProducts[0])}
            onClick={() => handleImageClick(0, openMarketProducts[0])}
          />
          {openMarketProducts[0].status === 'ONSALE' && (
            <ChipWrapper>
              <Chip type={'square'} theme={'strong'} color={'primary'} size={'lg'}>
                {'판매 중인 매물'}
              </Chip>
            </ChipWrapper>
          )}
        </SingleImageWrapper>

        {imageViewerVisible && (
          <ImageViewer.Multi
            ref={imageViewerRef}
            visible={imageViewerVisible}
            images={getSortedImages(openMarketProducts[0])}
            defaultIndex={currentImageViewerIndex}
            onClose={handleImageViewerClose}
            onIndexChange={(index) => {
              setPreviousImageViewerIndex(index);
              setCurrentImageViewerIndex(index);
            }}
            getContainer={() => document.getElementById('root-modal') || document.body}
            renderFooter={(image, index) => {
              const currentImages = getSortedImages(openMarketProducts[0]);
              return (
                <ImageViewerControls>
                  <ImageViewerTopBar>
                    <CloseButton onClick={handleCloseButtonClick}>{'✕'}</CloseButton>
                  </ImageViewerTopBar>
                  <NavButtonLeft onClick={handlePrevImage} disabled={index === 0}>
                    <svg
                      width={'24'}
                      height={'24'}
                      viewBox={'0 0 24 24'}
                      fill={'none'}
                      xmlns={'http://www.w3.org/2000/svg'}
                    >
                      <path
                        d={'M15 18L9 12L15 6'}
                        stroke={'currentColor'}
                        strokeWidth={'2'}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                      />
                    </svg>
                  </NavButtonLeft>
                  <NavButtonRight onClick={handleNextImage} disabled={index === currentImages.length - 1}>
                    <svg
                      width={'24'}
                      height={'24'}
                      viewBox={'0 0 24 24'}
                      fill={'none'}
                      xmlns={'http://www.w3.org/2000/svg'}
                    >
                      <path
                        d={'M9 18L15 12L9 6'}
                        stroke={'currentColor'}
                        strokeWidth={'2'}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                      />
                    </svg>
                  </NavButtonRight>
                  <ImageViewerBottomBar>
                    <ImageCounter>{`${index + 1} / ${currentImages.length}`}</ImageCounter>
                  </ImageViewerBottomBar>
                </ImageViewerControls>
              );
            }}
          />
        )}
      </>
    );
  }

  return null;
};

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  width: 100%;
`;

const ImageWrapper = styled.div`
  position: relative;
  min-width: 300px;
  width: 300px;
  height: 255px;
  cursor: pointer;
`;

const SingleImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const CustomImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const ChipWrapper = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
`;

const ImageViewerControls = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1000;

  @media screen and (min-width: 512px) {
    max-width: 100vw;
  }
`;

const ImageViewerTopBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px;
  pointer-events: auto;
`;

const CloseButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:active {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const ImageViewerBottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  pointer-events: auto;
`;

const ImageCounter = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 20px;
`;

const NavButtonLeft = styled.button<{ disabled?: boolean }>`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  pointer-events: auto;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)')};
  }

  &:active {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.9)')};
  }

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const NavButtonRight = styled.button<{ disabled?: boolean }>`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  pointer-events: auto;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)')};
  }

  &:active {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.9)')};
  }

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}
`;
